import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { call, callTk } from 'api';
import type { CompetitionAdvancedSearchResultType } from 'types/competition';

export type CompetitionAdvancedSearchCallType = {
  page: number;
  results: CompetitionAdvancedSearchResultType[];
  results_numbers: number;
  total_pages: number;
};

export type FiltersType = {
  age_category: string;
  championship: string;
  date_from: Date;
  date_to: Date;
  gender: string;
  query: string;
  region: string;
  province: string[];
  status: string;
};

export type SavedCompetitionSearchType = {
  // id: number;
  // user_id: number;
  championship: boolean;
  age_category: 'open' | 'under' | 'over' | '';
  rankings_category: 'open' | 'quarta' | 'terza' | 'seconda' | '';
  gender: number | string;
  region: string;
  province: string[];
  // created_at: string;
  // updated_at: string;
};

export const fetch = createAsyncThunk<CompetitionAdvancedSearchCallType, FiltersType>(
  'competitions/advanced-search/fetch',
  async (filters, { rejectWithValue }) => {
    const response = await callTk<CompetitionAdvancedSearchCallType>(
      'competitions/advanced-search',
      {
        method: 'POST',
        body: JSON.stringify({
          ...filters,
        }),
      },
      (error) => rejectWithValue(error),
      true,
    );
    return response;
  },
);

export const fetchSavedCompetitionSearch = createAsyncThunk<SavedCompetitionSearchType>(
  'fetch-saved-competition-search',
  async (_, { rejectWithValue }) => {
    const response = await callTk<SavedCompetitionSearchType>(
      'saved-competition-search',
      {
        method: 'GET',
      },
      (error) => {
        if (error.status === 404) {
          return null;
        }
        rejectWithValue(error);
      },
      true,
    );
    return response;
  },
);

export const saveSavedCompetitionSearch = createAsyncThunk<{ success: boolean }, SavedCompetitionSearchType>(
  'save-saved-competition-search',
  async (params, { rejectWithValue, dispatch }) => {
    const response = await call<{ success: boolean }>(
      'saved-competition-search',
      {
        method: 'POST',
        body: JSON.stringify({
          ...params,
        }),
      },
      (response: { success: boolean }) => {
        dispatch(setSavedCompetitionSearch(params));
        return response;
      },
      (error) => rejectWithValue(error),
      false,
    );
    return response;
  },
);

export const deleteSavedCompetitionSearch = createAsyncThunk<{ success: boolean }, void>(
  'delete-saved-competition-search',
  async (_, { rejectWithValue, dispatch }) => {
    const response = await call<{ success: boolean }>(
      'saved-competition-search',
      {
        method: 'DELETE',
      },
      (response: { success: boolean }) => {
        dispatch(resetSavedCompetitionSearch());
        return response;
      },
      (error) => rejectWithValue(error),
      false,
    );
    return response;
  },
);

export const competitionAdvancedSearchSlice = createSlice({
  name: 'competitionAdvancedSearch',
  initialState: {
    loading: false,
    error: false,
    data: {
      page: 1,
      total_pages: 0,
      results: [] as CompetitionAdvancedSearchResultType[],
      results_numbers: 0,
    },
    savedCompetitionSearch: null as SavedCompetitionSearchType | null,
  },
  reducers: {
    setSavedCompetitionSearch: (state, action: PayloadAction<SavedCompetitionSearchType>) => {
      state.savedCompetitionSearch = action.payload;
    },
    resetSavedCompetitionSearch: (state) => {
      state.savedCompetitionSearch = null;
    },
    clearCompetitionsAdvancedSearchResults: (state) => {
      state.data.page = 1;
      state.data.total_pages = 0;
      state.data.results = [];
      state.data.results_numbers = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetch.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetch.fulfilled, (state, action: PayloadAction<CompetitionAdvancedSearchCallType>) => {
      state.loading = false;
      state.data.page = action.payload.page;
      state.data.total_pages = action.payload.total_pages;
      state.data.results.push(...action.payload.results);
      state.data.results_numbers = action.payload.results_numbers;
    });
    builder.addCase(fetch.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchSavedCompetitionSearch.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(
      fetchSavedCompetitionSearch.fulfilled,
      (state, action: PayloadAction<SavedCompetitionSearchType>) => {
        state.loading = false;
        state.savedCompetitionSearch = action.payload;
      },
    );
    builder.addCase(fetchSavedCompetitionSearch.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(saveSavedCompetitionSearch.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(saveSavedCompetitionSearch.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(saveSavedCompetitionSearch.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(deleteSavedCompetitionSearch.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteSavedCompetitionSearch.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(deleteSavedCompetitionSearch.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export const { setSavedCompetitionSearch, resetSavedCompetitionSearch, clearCompetitionsAdvancedSearchResults } =
  competitionAdvancedSearchSlice.actions;
export const competitionAdvancedSearchReducer = competitionAdvancedSearchSlice.reducer;
